import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { ThemeContext } from '../context/ThemeContext';
import { useApi } from '../hooks/useApi';
import { gsap } from 'gsap';
import {
  FiCheck,
  FiLock,
  FiArrowRight,
  FiShield,
  FiCalendar,
} from 'react-icons/fi';
import MarketingLayout from '../layouts/MarketingLayout';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import Ripple from '../components/Ripple';
import SpotlightCard from '../components/SpotlightCard';
import { getOcrLimitLabel } from '../constants/ocrPlans';
import AcceptTermsField from '../components/legal/AcceptTermsField';
import { readApiJson } from '../utils/apiResponse';
import { fetchPublicPlans } from '../utils/fetchPlans';
import MarketingPageAlerts from '../components/marketing/MarketingPageAlerts';
import { startSubscriptionCheckout } from '../utils/mercadopagoCheckout';
import {
  formatPlanPrice,
  getPlanBillingPeriodLabel,
  filterProductPlans,
} from '../utils/planUtils';
import { needsSubscriptionPayment, guestNeedsOnboarding, mustRegularizeSubscription, getSubscriptionStatusPresentation } from '../utils/subscriptionGate';
import { getSubscriptionMoraBannerMessage } from '../utils/subscriptionAccess';
import { FormAlert } from '../components/form';

const Planes = () => {
  const { user } = useContext(AuthContext);
  const { modoOscuro } = useContext(ThemeContext);
  const { secureFetch } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paymentPendingBanner =
    searchParams.get('pago') === 'pendiente' || needsSubscriptionPayment(user);

  const moraBannerMessage = getSubscriptionMoraBannerMessage(user, { includeBlocked: true });
  const regularizePayment = mustRegularizeSubscription(user);
  const subscriptionStatusUi = getSubscriptionStatusPresentation(user);

  const subscriptionStatusBadgeClass = {
    success: modoOscuro ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-700',
    warning: modoOscuro ? 'bg-amber-900/50 text-amber-200' : 'bg-amber-100 text-amber-800',
    danger: modoOscuro ? 'bg-red-900/50 text-red-200' : 'bg-red-100 text-red-800',
    muted: modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600',
  }[subscriptionStatusUi.tone] || (modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600');

  const paymentPendingAlertText = (() => {
    if (!user || moraBannerMessage) return null;
    if (!paymentPendingBanner) return null;
    const status = String(user.subscription?.status || '').toLowerCase();
    if (status === 'pending_payment' || guestNeedsOnboarding(user)) {
      return 'Tu registro quedó a medias: la empresa en Contsuite se crea cuando autorices el plan en Mercado Pago. Si cerraste la ventana, reintentá el pago desde acá.';
    }
    return 'Regularizá tu suscripción para continuar usando Contsuite.';
  })();

  const box = modoOscuro ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
  const textPri = modoOscuro ? 'text-white' : 'text-gray-900';
  const textSec = modoOscuro ? 'text-gray-300' : 'text-gray-600';
  const textMuted = modoOscuro ? 'text-gray-400' : 'text-gray-500';
  const btnOut = modoOscuro ? 'border-gray-600 text-white hover:bg-gray-800' : 'border-gray-300 text-gray-800 hover:bg-gray-50';

  // Colores específicos para hero (sobre Aurora)
  const heroText = modoOscuro ? 'text-white' : 'text-black';
  const heroTextMuted = modoOscuro ? 'text-white/80' : 'text-black/80';
  const tableHead = modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-500';
  const tableRowAlt = modoOscuro ? 'bg-gray-800/50' : 'bg-gray-50/70';
  const divideY = modoOscuro ? 'divide-gray-700' : 'divide-gray-100';
  const iconBox = modoOscuro ? 'bg-[#004AAD]/30 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]';
  const heroBtnPrimary = modoOscuro
    ? 'bg-white text-[#004aad] border-white hover:bg-[#00357a] hover:text-white hover:border-[#00357a]'
    : 'bg-black text-white border-black hover:bg-gray-800';
  const heroBtnSecondary = modoOscuro
    ? 'border-2 border-white text-white hover:bg-white hover:text-[#004aad]'
    : 'border-2 border-black text-black hover:bg-black hover:text-white';
  const currentPlanId =
    typeof user?.currentPlan === 'string'
      ? user.currentPlan
      : user?.currentPlan?._id || user?.currentPlan?.id;

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [userPlan, setUserPlan] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Refs para secciones
  const section1Ref = useRef(null);
  const section2Ref = useRef(null);
  const section1ContentRef = useRef(null);
  const section2ContentRef = useRef(null);
  const activeSectionRef = useRef(0);

  useEffect(() => {
    activeSectionRef.current = 0;
    window.__planesActiveSection = 0;
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    setErrorMessage('');
    const result = await fetchPublicPlans();
    if (result.ok) {
      setPlans(result.plans);
    } else {
      setPlans([]);
      setErrorMessage(result.message);
    }
    setLoading(false);
  };

  const loadUserPlan = async () => {
    if (!user?.currentPlan) return;

    // currentPlan puede venir como ID (string) o como objeto populado
    const planId =
      typeof user.currentPlan === 'string'
        ? user.currentPlan
        : user.currentPlan._id || user.currentPlan.id;

    if (!planId) return;

    try {
      const response = await secureFetch(`/api/plans/${planId}`);
      if (response.ok) {
        const data = await readApiJson(response);
        setUserPlan(data);
      }
    } catch (error) {
      console.error('Error al cargar plan del usuario:', error);
    }
  };

  useEffect(() => {
    loadPlans();
    loadUserPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.fromTo('.hero-badge', { opacity: 0, y: -30 }, { opacity: 1, y: 0, duration: 0.6 });
      tl.fromTo('.hero-title', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 0.8 }, '-=0.3');
      tl.fromTo('.hero-desc', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6 }, '-=0.4');
      tl.fromTo('.hero-cta', { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 0.6 }, '-=0.3');
      tl.fromTo('.hero-plans > *', { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.45, stagger: 0.1 }, '-=0.3');
    }, section1Ref);
    return () => ctx.revert();
  }, [loading]);

  useEffect(() => {
    const container = document.getElementById('main-scroll');
    if (!container) return;

    const sections = [section1Ref, section2Ref];

    const handleScroll = () => {
      const marker = container.scrollTop + container.clientHeight * 0.3;
      let current = 0;

      sections.forEach((ref, index) => {
        const el = ref.current;
        if (!el) return;
        if (el.offsetTop <= marker) current = index;
      });

      if (activeSectionRef.current !== current) {
        activeSectionRef.current = current;
        window.__planesActiveSection = current;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, [loading]);

  // Sin animaciones de contenido al cambiar de sección (eliminadas para consistencia)

  const handleSubscribe = (plan) => {
    setErrorMessage('');
    setSuccessMessage('');
    if (!user) {
      navigate('/register');
      return;
    }
    setAcceptTerms(false);
    setSelectedPlan(plan);
  };

  const confirmSubscription = async () => {
    if (!selectedPlan) return;
    if (!acceptTerms) {
      setErrorMessage('Debés aceptar los términos y la política de privacidad para continuar.');
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    setCheckoutLoading(true);
    try {
      const renewingSamePlan =
        regularizePayment && currentPlanId && selectedPlan._id === currentPlanId;
      await startSubscriptionCheckout({
        request: secureFetch,
        planId: selectedPlan._id,
        billingCycle,
        source: renewingSamePlan || isPlanUpgrade(selectedPlan) || user?.currentPlan ? 'upgrade' : 'signup',
      });
    } catch (error) {
      console.error('Error al iniciar checkout:', error);
      setErrorMessage(
        error.message || 'No pudimos iniciar el pago. Verificá Mercado Pago o probá más tarde.'
      );
    } finally {
      setCheckoutLoading(false);
    }
  };

  const isPlanUpgrade = (plan) => {
    if (!userPlan) return false;
    const currentStatus = userPlan.slug;
    const planStatus = plan.slug;

    const planHierarchy = {
      basico: 1,
      profesional: 2,
      empresarial: 3,
      enterprise: 4,
    };

    return (planHierarchy[planStatus] || 0) > (planHierarchy[currentStatus] || 0);
  };

  const getFeatureName = (feature, { short = false } = {}) => {
    const featureNames = {
      basicInvoicing: 'Registro de ventas',
      registroEgresos: 'Registro de gastos con categorías',
      basicTemplates: 'Plantillas de registro',
      customTemplates: 'Diseño personalizado de registros',
      companyBranding: 'Marca de tu negocio',
      employeeAccounts: 'Múltiples usuarios',
      multiCompany: 'Varios negocios en una cuenta',
      advancedAnalytics: 'Panel principal y reportes avanzados',
      prioritySupport: 'Soporte prioritario',
      multimoneda: 'Pesos, dólares y euros en el mismo lugar',
      presupuestos: 'Presupuestos y alertas de metas',
      flujoDeCaja: 'Flujo de caja proyectado',
      reportesAutomatizados: 'Resumen mensual y semanal por email (opt-in, lunes)',
      exportacionCsv: 'Descarga para Excel (ventas, clientes, gastos)',
      estadoResultados: 'Cuánto ganaste o perdiste en el período',
      orientacionFiscal: 'Panel fiscal: Monotributo, IVA y cómo vas respecto al tope',
      resumenClientes: 'Por cliente: facturado, cobrado y pendiente',
      validacionCuit: 'Control del CUIT al cargar clientes',
      recordatoriosCobranza: 'Recordatorios masivos de cobranza por email',
      importCsvEgresos: 'Importar gastos desde una planilla',
      duplicarMovimientos: 'Duplicar última venta o gasto',
      recurrentesMensuales: 'Ventas y gastos recurrentes (se crean solos)',
      cobranzasSeguimiento: 'Deuda impaga, historial de gestiones y promesas manuales',
      mediosCobro: 'Cuenta → Cobrar: alias, CBU, link y plantillas de WhatsApp',
      cicloCobroMes: 'Ciclo del mes: emitido, cobrado y pendiente (Inicio y Cobrar)',
      asistenteContextual: 'Asistente con preguntas frecuentes en Inicio y Cobrar',
      pagosParciales: 'Pagos parciales y saldo real',
      bandejaMes: 'Bandeja del día en Inicio',
      packContador: 'Paquete mensual para el contador (archivo + PDF)',
      comprobantePdfPro: 'Comprobante en PDF y envío por email',
      secuenciasCobranza: 'Recordatorios automáticos de cobro (+3/+7/+15 días)',
      importMercadoPago: 'Traer cobros de Mercado Pago como ventas',
      scoreCobranza: 'Indicador por cliente de quién paga tarde (0–100)',
      vistaContador: 'Pantalla para tu contador (solo lectura)',
      calendarioFiscal: 'Calendario de vencimientos con avisos',
      simuladorFiscal: 'Simulador de categoría Monotributo',
      checklistFiscal: 'Lista de tareas fiscales del mes',
      ocrTickets: 'Escaneo de comprobante → gasto cargado',
      rolLector: 'Usuario de solo lectura para el equipo',
      alertasPromesas: 'Avisos si se vence una promesa de pago',
      packContadorAuto: 'Envío automático mensual del paquete al contador',
      conciliacionBancaria: 'Cruzar extracto del banco con tus movimientos',
      hubCobranzas: 'Cobrar: quién te debe, promesas y prioridad del día',
      linkPagoMp: 'Generar o abrir link de Mercado Pago por factura',
      invitarContador: 'Invitar a tu contador (solo lectura) por email',
      kpisCobranzaReportes: 'KPIs de cobranza en Reportes (aging y tasa)',
      margenPorCliente: 'Cuánto te deja cada cliente (con gastos asignados)',
      tendenciasCobranza: 'DSO, gráfico de 6 meses y comparación mes a mes',
      alertasCobranza: 'Avisos por mail si empeora el cobro',
      exportCobranzasCsv: 'Descargar análisis de cobranzas para Excel',
      widgetCobranzaInicio: 'Resumen de cobranza en Inicio (tasa, pendiente e impagas)',
    };
    const shortNames = {
      registroEgresos: 'Registro de gastos',
      customTemplates: 'Diseño personalizado',
      advancedAnalytics: 'Panel y reportes',
      multiCompany: 'Varios negocios',
      reportesAutomatizados: 'Resúmenes email mensual/semanal',
      exportacionCsv: 'Descarga Excel',
      estadoResultados: 'Resultado del período',
      orientacionFiscal: 'Panel fiscal y tope',
      resumenClientes: 'Resumen por cliente',
      multimoneda: 'Pesos, dólares y euros',
      validacionCuit: 'Control de CUIT',
      recordatoriosCobranza: 'Recordatorios email',
      importCsvEgresos: 'Importar gastos (planilla)',
      duplicarMovimientos: 'Duplicar último movimiento',
      recurrentesMensuales: 'Recurrentes mensuales',
      cobranzasSeguimiento: 'Gestiones manuales',
      mediosCobro: 'Medios de cobro',
      cicloCobroMes: 'Ciclo del mes',
      asistenteContextual: 'Asistente',
      pagosParciales: 'Pagos parciales',
      bandejaMes: 'Bandeja del día',
      packContador: 'Paquete contador',
      comprobantePdfPro: 'PDF + email',
      secuenciasCobranza: 'Recordatorios +3/+7/+15',
      importMercadoPago: 'Mercado Pago → ventas',
      scoreCobranza: 'Indicador por cliente',
      vistaContador: 'Pantalla contador',
      calendarioFiscal: 'Calendario vencimientos',
      simuladorFiscal: 'Simulador Monotributo',
      checklistFiscal: 'Tareas fiscales del mes',
      ocrTickets: 'Escaneo → gasto',
      rolLector: 'Solo lectura equipo',
      alertasPromesas: 'Avisos de promesas',
      packContadorAuto: 'Paquete mensual auto',
      conciliacionBancaria: 'Conciliar banco',
      hubCobranzas: 'Pantalla Cobrar',
      linkPagoMp: 'Link Mercado Pago',
      invitarContador: 'Invitar contador',
      kpisCobranzaReportes: 'Cobranzas en Reportes',
      margenPorCliente: 'Rentabilidad por cliente',
      tendenciasCobranza: 'DSO y tendencias',
      alertasCobranza: 'Avisos de cobro',
      exportCobranzasCsv: 'Exportar cobranzas',
      widgetCobranzaInicio: 'Resumen en Inicio',
    };
    if (short && shortNames[feature]) return shortNames[feature];
    return featureNames[feature] || feature;
  };

  const featureGroups = [
    {
      title: 'Gestión diaria',
      features: [
        'basicInvoicing',
        'registroEgresos',
        'duplicarMovimientos',
        'recurrentesMensuales',
        'bandejaMes',
        'basicTemplates',
        'comprobantePdfPro',
      ],
    },
    {
      title: 'Clientes y cobranzas',
      features: [
        'validacionCuit',
        'resumenClientes',
        'hubCobranzas',
        'mediosCobro',
        'cobranzasSeguimiento',
        'pagosParciales',
        'cicloCobroMes',
        'widgetCobranzaInicio',
        'kpisCobranzaReportes',
        'linkPagoMp',
        'scoreCobranza',
        'tendenciasCobranza',
        'exportCobranzasCsv',
        'secuenciasCobranza',
        'recordatoriosCobranza',
        'alertasPromesas',
        'alertasCobranza',
        'asistenteContextual',
      ],
    },
    {
      title: 'Reportes y finanzas',
      features: [
        'flujoDeCaja',
        'estadoResultados',
        'advancedAnalytics',
        'reportesAutomatizados',
        'conciliacionBancaria',
        'margenPorCliente',
        'presupuestos',
      ],
    },
    {
      title: 'Fiscal y contador',
      features: [
        'calendarioFiscal',
        'orientacionFiscal',
        'simuladorFiscal',
        'checklistFiscal',
        'packContador',
        'vistaContador',
        'packContadorAuto',
        'invitarContador',
      ],
    },
    {
      title: 'Importar, exportar y escanear',
      features: [
        'importCsvEgresos',
        'exportacionCsv',
        'importMercadoPago',
        'ocrTickets',
      ],
    },
    {
      title: 'Equipo y personalización',
      features: [
        'customTemplates',
        'companyBranding',
        'multimoneda',
        'employeeAccounts',
        'rolLector',
        'multiCompany',
        'prioritySupport',
      ],
    },
  ];

  const planFeatureMatrix = {
    basico: {
      basicInvoicing: true,
      registroEgresos: true,
      duplicarMovimientos: true,
      recurrentesMensuales: true,
      validacionCuit: true,
      resumenClientes: true,
      basicTemplates: true,
      exportacionCsv: true,
      cobranzasSeguimiento: true,
      pagosParciales: true,
      bandejaMes: true,
      calendarioFiscal: true,
      hubCobranzas: true,
      mediosCobro: true,
      cicloCobroMes: true,
      kpisCobranzaReportes: true,
      widgetCobranzaInicio: true,
    },
    profesional: {
      basicInvoicing: true,
      registroEgresos: true,
      duplicarMovimientos: true,
      recurrentesMensuales: true,
      importCsvEgresos: true,
      validacionCuit: true,
      resumenClientes: true,
      basicTemplates: true,
      customTemplates: true,
      companyBranding: true,
      multimoneda: true,
      exportacionCsv: true,
      packContador: true,
      vistaContador: true,
      cobranzasSeguimiento: true,
      pagosParciales: true,
      comprobantePdfPro: true,
      secuenciasCobranza: true,
      scoreCobranza: true,
      bandejaMes: true,
      calendarioFiscal: true,
      flujoDeCaja: true,
      estadoResultados: true,
      orientacionFiscal: true,
      simuladorFiscal: true,
      checklistFiscal: true,
      advancedAnalytics: true,
      recordatoriosCobranza: true,
      reportesAutomatizados: true,
      importMercadoPago: true,
      alertasPromesas: true,
      packContadorAuto: true,
      conciliacionBancaria: true,
      hubCobranzas: true,
      mediosCobro: true,
      cicloCobroMes: true,
      linkPagoMp: true,
      invitarContador: true,
      kpisCobranzaReportes: true,
      margenPorCliente: true,
      tendenciasCobranza: true,
      exportCobranzasCsv: true,
      widgetCobranzaInicio: true,
      alertasCobranza: true,
      asistenteContextual: true,
      ocrTickets: true,
      prioritySupport: true,
    },
    empresarial: {
      basicInvoicing: true,
      registroEgresos: true,
      duplicarMovimientos: true,
      recurrentesMensuales: true,
      importCsvEgresos: true,
      validacionCuit: true,
      resumenClientes: true,
      basicTemplates: true,
      customTemplates: true,
      companyBranding: true,
      multimoneda: true,
      exportacionCsv: true,
      packContador: true,
      vistaContador: true,
      cobranzasSeguimiento: true,
      pagosParciales: true,
      comprobantePdfPro: true,
      secuenciasCobranza: true,
      scoreCobranza: true,
      bandejaMes: true,
      calendarioFiscal: true,
      flujoDeCaja: true,
      estadoResultados: true,
      orientacionFiscal: true,
      simuladorFiscal: true,
      checklistFiscal: true,
      advancedAnalytics: true,
      recordatoriosCobranza: true,
      reportesAutomatizados: true,
      importMercadoPago: true,
      alertasPromesas: true,
      packContadorAuto: true,
      conciliacionBancaria: true,
      hubCobranzas: true,
      mediosCobro: true,
      cicloCobroMes: true,
      linkPagoMp: true,
      invitarContador: true,
      kpisCobranzaReportes: true,
      margenPorCliente: true,
      tendenciasCobranza: true,
      exportCobranzasCsv: true,
      widgetCobranzaInicio: true,
      alertasCobranza: true,
      asistenteContextual: true,
      ocrTickets: true,
      employeeAccounts: true,
      rolLector: true,
      multiCompany: true,
      presupuestos: true,
      prioritySupport: true,
    },
  };

  const isFeatureEnabledForPlan = (plan, featureKey) => {
    const slug = (plan.slug || '').toLowerCase();
    return Boolean(planFeatureMatrix[slug]?.[featureKey]);
  };
  // Solo mostramos los 3 planes de producto (Básico, Profesional, Empresarial)
  // y los ordenamos por precio ascendente.
  const orderedPlans = useMemo(() => filterProductPlans(plans), [plans]);

  const openRenewalCheckout = () => {
    const planFromList = orderedPlans.find((p) => p._id === currentPlanId);
    const plan = planFromList || (userPlan && currentPlanId
      ? { ...userPlan, _id: userPlan._id || userPlan.id || currentPlanId }
      : null);
    if (plan) handleSubscribe(plan);
    else setErrorMessage('No encontramos tu plan actual. Elegí un plan de la lista o contactanos.');
  };

  const getPlanTagline = (plan) => {
    const slug = (plan.slug || '').toLowerCase();
    if (slug === 'basico') {
      return 'Cobrar con WhatsApp, resumen básico en Reportes y bandeja del día. 30 días gratis.';
    }
    if (slug === 'profesional') {
      return 'Cobranzas completas, 40 escaneos de comprobantes por mes, rentabilidad por cliente, avisos e invitar a tu contador en solo lectura.';
    }
    if (slug === 'empresarial') {
      return 'Todo Profesional + 150 escaneos de tickets por mes, equipo, varios negocios y presupuestos con avisos.';
    }
    return plan.description || '';
  };

  const getPlanBulletPoints = (plan) => {
    const slug = (plan.slug || '').toLowerCase();
    if (slug === 'basico') {
      const maxCli = plan.maxClients ?? 20;
      const maxDoc = plan.maxDocumentsPerMonth ?? 50;
      return [
        'Ventas, gastos y movimientos que se repiten solos',
        'Cobrar: impagas, WhatsApp, gestiones y pagos parciales',
        'Resumen básico de cobranzas y calendario de vencimientos',
        `Hasta ${maxCli} clientes y ${maxDoc} ventas/gastos por mes · 30 días gratis`,
      ];
    }
    if (slug === 'profesional') {
      return [
        'Todo Básico + 40 escaneos de comprobantes por mes',
        'Gráficos de cobro, días para cobrar y descarga para Excel',
        'Link de Mercado Pago, indicador por cliente y recordatorios automáticos',
        'Resultado del mes, varias monedas, banco y paquete para tu contador',
      ];
    }
    if (slug === 'empresarial') {
      return [
        'Todo Profesional + 150 escaneos de tickets por mes',
        'Hasta 10 usuarios con solo lectura y hasta 5 negocios',
        'Presupuestos por categoría con avisos si te desviás',
      ];
    }
    return [];
  };

  const scrollToComparison = () => {
    section2Ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const shortPlanName = (plan) => {
    const slug = (plan.slug || '').toLowerCase();
    if (slug === 'profesional') return 'Pro';
    if (slug === 'empresarial') return 'Empresa';
    return plan.name;
  };

  const limitRows = [
    { key: 'users', label: 'Usuarios incluidos', value: (plan) => plan.maxEmployees },
    { key: 'clients', label: 'Clientes en tu cartera', value: (plan) => plan.maxClients || '—' },
    { key: 'flows', label: 'Plantillas de registro', value: (plan) => plan.maxTemplates },
    {
      key: 'docs',
      label: 'Ventas y gastos por mes',
      value: (plan) => {
        const n = Number(plan.maxDocumentsPerMonth);
        if (!Number.isFinite(n) || n <= 0) return 'Sin tope mensual';
        return `Hasta ${n}`;
      },
    },
    { key: 'ocr', label: 'Escaneos de comprobantes (por mes)', value: (plan) => getOcrLimitLabel(plan.slug) },
  ];

  const renderFeatureMark = (enabled) => (
    enabled ? (
      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${modoOscuro ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-600'}`}>
        <FiCheck className="h-3.5 w-3.5" />
      </span>
    ) : (
      <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full ${modoOscuro ? 'bg-gray-800 text-gray-600' : 'bg-gray-100 text-gray-400'}`}>
        <FiLock className="h-3.5 w-3.5" />
      </span>
    )
  );

  const openContact = () => {
    window.dispatchEvent(new Event('openContactModal'));
  };

  const showPageAlerts =
    Boolean(errorMessage || successMessage || paymentPendingAlertText);

  if (loading) {
    return (
      <MarketingLayout>
        <div className={`fixed inset-0 z-50 flex flex-col items-center justify-center ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
          {/* Logo pulse animation */}
          <div className="relative">
            <div className="animate-pulse">
              <img
                src={modoOscuro ? "/logowhite.png" : "/logoblack.png"}
                alt="Contsuite"
                className="h-12 w-auto"
              />
            </div>
            {/* Ripple rings */}
            <div className="absolute inset-0 -m-8">
              <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-500/30' : 'border border-blue-500/30'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite]`} />
              <div className={`absolute inset-0 rounded-full ${modoOscuro ? 'border border-blue-400/20' : 'border border-blue-400/20'} animate-[ping_2s_cubic-bezier(0,0,0.2,1)_infinite_0.5s]`} />
            </div>
          </div>
          {/* Loading text */}
          <div className="mt-8 text-center space-y-2">
            <p className={`text-sm font-medium ${textPri}`}>
              Cargando planes
            </p>
            <p className={`text-xs ${textSec}`}>
              Preparando las mejores opciones para vos...
            </p>
          </div>
          {/* Progress dots */}
          <div className="mt-6 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '0ms' }} />
            <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '150ms' }} />
            <div className={`w-2 h-2 rounded-full ${modoOscuro ? 'bg-blue-400' : 'bg-blue-600'} animate-bounce`} style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </MarketingLayout>
    );
  }

  return (
    <MarketingLayout>
      <div className="bg-transparent">
        <section ref={section1Ref} className={`relative flex flex-col justify-start overflow-x-hidden py-8 sm:py-10 lg:py-12 ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
          <Ripple
            mainCircleSize={300}
            numCircles={6}
            className={modoOscuro ? 'text-[#1F80FF]/50' : 'text-[#004AAD]/25'}
          />
          {showPageAlerts && (
            <MarketingPageAlerts>
              {paymentPendingAlertText && (
                <FormAlert type="warning" className="mb-3">
                  {paymentPendingAlertText}
                </FormAlert>
              )}
              {errorMessage && (
                <FormAlert
                  type="error"
                  className="mb-3"
                  action={
                    <button
                      type="button"
                      onClick={loadPlans}
                      className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold border transition-colors ${
                        modoOscuro
                          ? 'border-red-600/50 text-red-100 hover:bg-red-900/40'
                          : 'border-red-300 text-red-800 hover:bg-red-100'
                      }`}
                    >
                      Reintentar
                    </button>
                  }
                >
                  {errorMessage}
                </FormAlert>
              )}
              {successMessage && (
                <FormAlert type="success" className="mb-3">
                  {successMessage}
                </FormAlert>
              )}
            </MarketingPageAlerts>
          )}
          <div
            ref={section1ContentRef}
            className={`relative z-10 w-full pb-10 sm:pb-12 ${
              showPageAlerts ? 'pt-2 sm:pt-4' : 'pt-12 sm:pt-14 lg:pt-12'
            }`}
          >
            <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 text-center mb-6 sm:mb-8">
              <p className={`hero-badge text-[11px] font-semibold tracking-[0.18em] uppercase mb-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                Planes y precios
              </p>
              <h1 className={`hero-title text-2xl sm:text-3xl md:text-4xl font-extrabold leading-[1.12] tracking-tight mb-2 ${heroText}`}>
                Elegí el plan que ordena tu mes
              </h1>
              <p className={`hero-desc text-sm sm:text-base leading-relaxed max-w-2xl mx-auto ${heroTextMuted}`}>
                Precios en pesos argentinos. Básico para empezar a registrar y cobrar. Profesional para cobranzas completas, paquete para tu contador y más reportes. Empresarial para equipo y varios negocios.
              </p>

              <div className="hero-cta flex flex-col items-center gap-2.5 mt-4 sm:mt-5">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5">
                  {user && regularizePayment ? (
                    <button
                      type="button"
                      onClick={openRenewalCheckout}
                      className={`group px-5 py-2.5 rounded-xl font-semibold text-sm border transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 ${heroBtnPrimary}`}
                    >
                      Regularizar pago en Mercado Pago
                      <FiArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ) : user && userPlan && !guestNeedsOnboarding(user) ? (
                    <button
                      type="button"
                      onClick={() => navigate('/inicio')}
                      className={`group px-5 py-2.5 rounded-xl font-semibold text-sm border transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 ${heroBtnPrimary}`}
                    >
                      Ir a mi cuenta
                      <FiArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => navigate('/register')}
                      className={`group px-5 py-2.5 rounded-xl font-semibold text-sm border transition-all duration-300 shadow-lg hover:shadow-xl active:scale-95 ${heroBtnPrimary}`}
                    >
                      Empezar gratis
                      <FiArrowRight className="inline-block ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={openContact}
                    className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 active:scale-95 ${heroBtnSecondary}`}
                  >
                    Hablar con nosotros
                  </button>
                </div>

                {userPlan && (
                  <div className={`inline-flex items-center gap-3 rounded-full px-4 py-2 shadow-sm border text-xs ${box} ${textSec}`}>
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-lg ${iconBox}`}>
                      <FiShield className="w-3 h-3" />
                    </span>
                    <span className="font-medium">Tu plan actual:</span>
                    <span className={`font-semibold ${textPri}`}>{userPlan.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${subscriptionStatusBadgeClass}`}>
                      {subscriptionStatusUi.label}
                    </span>
                  </div>
                )}

                <div className={`flex items-center justify-center gap-3 p-1.5 rounded-full ${modoOscuro ? 'bg-gray-800/70' : 'bg-gray-100'}`}>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      billingCycle === 'monthly'
                        ? modoOscuro
                          ? 'bg-gray-700 text-white shadow-md'
                          : 'bg-white text-gray-900 shadow-md'
                        : modoOscuro
                          ? 'text-gray-400 hover:text-white'
                          : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Mensual
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('annual')}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                      billingCycle === 'annual'
                        ? modoOscuro
                          ? 'bg-green-600 text-white shadow-md'
                          : 'bg-green-500 text-white shadow-md'
                        : modoOscuro
                          ? 'text-gray-400 hover:text-white'
                          : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <FiCalendar className="w-4 h-4" />
                    Anual
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      billingCycle === 'annual'
                        ? 'bg-white/20'
                        : modoOscuro
                          ? 'bg-green-900/50 text-green-300'
                          : 'bg-green-100 text-green-600'
                    }`}>
                      -17%
                    </span>
                  </button>
                </div>
                <p className={`text-xs ${textMuted}`}>
                  {billingCycle === 'annual' ? 'Ahorrá 2 meses pagando anualmente' : 'Podés cambiar a anual cuando quieras'}
                </p>
              </div>
            </div>

            <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pb-4">
              {orderedPlans.length === 0 && !errorMessage && (
                <p className={`text-center text-sm mb-6 ${textSec}`}>
                  No hay planes disponibles en este momento. Si el problema persiste, contactanos.
                </p>
              )}
              <div className="hero-plans grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6 items-stretch">
                {orderedPlans.map((plan) => {
                  const isPopular = plan.isPopular || (plan.slug || '').toLowerCase() === 'profesional';
                  const isCurrent = currentPlanId === plan._id;
                  const renewCurrentPlan = isCurrent && regularizePayment;
                  return (
                    <SpotlightCard
                      key={plan._id}
                      className={`relative flex h-full min-h-0 flex-col rounded-2xl border p-4 sm:p-5 text-left ${
                        isPopular
                          ? `${box} border-[#1F80FF] shadow-lg ring-1 ring-[#1F80FF]/20`
                          : `${box} shadow-sm`
                      }`}
                    >
                      <div className="mb-2 flex min-h-[2.75rem] items-start justify-between gap-2">
                        <h3 className={`text-base sm:text-lg font-bold leading-tight ${textPri}`}>
                          {plan.name}
                        </h3>
                        <div className="flex shrink-0 flex-col items-end gap-1">
                          {isPopular && (
                            <span className="inline-flex rounded-full bg-[#1F80FF] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white shadow-sm">
                              Más popular
                            </span>
                          )}
                          {isCurrent && (
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-white shadow-sm ${modoOscuro ? 'bg-gray-600' : 'bg-gray-900'}`}>
                              Tu plan
                            </span>
                          )}
                        </div>
                      </div>
                      <p className={`mb-3 text-sm leading-relaxed ${textSec}`}>
                        {getPlanTagline(plan)}
                      </p>

                      <div className="mb-3 flex flex-wrap items-baseline gap-x-1 gap-y-0.5">
                        <span className={`text-2xl sm:text-3xl font-bold ${textPri}`}>
                          {formatPlanPrice(plan, billingCycle)}
                        </span>
                        <span className={`text-xs sm:text-sm ${textMuted}`}>
                          /{getPlanBillingPeriodLabel(billingCycle)} ARS
                          {billingCycle === 'annual' ? ' · 2 meses bonificados' : ''}
                        </span>
                      </div>

                      {getPlanBulletPoints(plan).length > 0 && (
                        <ul className="mb-4 flex-1 space-y-2 text-sm min-h-0">
                          {getPlanBulletPoints(plan).map((item) => (
                            <li key={item} className="flex items-start gap-2">
                              <FiCheck className={`w-4 h-4 mt-0.5 shrink-0 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`} />
                              <span className={`leading-snug ${textSec}`}>{item}</span>
                            </li>
                          ))}
                        </ul>
                      )}

                      <button
                        type="button"
                        onClick={() => handleSubscribe(plan)}
                        disabled={isCurrent && !renewCurrentPlan}
                        className={`mt-auto w-full shrink-0 inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                          renewCurrentPlan
                            ? 'bg-[#1F80FF] text-white hover:bg-[#004AAD] hover:shadow-md'
                            : isCurrent
                            ? modoOscuro ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : isPopular
                            ? 'bg-[#1F80FF] text-white hover:bg-[#004AAD] hover:shadow-md'
                            : modoOscuro ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-900 text-white hover:bg-black'
                        }`}
                      >
                        {renewCurrentPlan
                          ? 'Regularizar pago'
                          : isCurrent
                          ? 'Plan actual'
                          : isPlanUpgrade(plan)
                          ? 'Subir de plan'
                          : 'Elegir este plan'}
                      </button>
                    </SpotlightCard>
                  );
                })}
              </div>

              <p className={`text-center mt-5 max-w-2xl mx-auto text-xs leading-relaxed ${textMuted}`}>
                Al suscribirte aceptás los{' '}
                <Link to="/terminos" className="text-[#1F80FF] hover:underline font-medium">
                  Términos de uso
                </Link>{' '}
                y la{' '}
                <Link to="/privacidad" className="text-[#1F80FF] hover:underline font-medium">
                  Política de privacidad
                </Link>
                . Pagos recurrentes procesados por Mercado Pago. Plan Básico: 30 días de prueba al registrarte.
              </p>

              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={scrollToComparison}
                  className={`text-sm font-medium underline underline-offset-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}
                >
                  Ver comparación completa ↓
                </button>
              </div>
            </div>
          </div>
        </section>

        <section ref={section2Ref} className={`relative py-16 lg:py-20 xl:py-24 ${modoOscuro ? 'bg-black' : 'bg-white'}`}>
          <div ref={section2ContentRef} className="w-full">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 xl:px-12">
            <div className="text-center mb-8 lg:mb-10">
              <p className={`text-[11px] font-semibold tracking-[0.2em] uppercase mb-2 ${modoOscuro ? 'text-[#5CA3FF]' : 'text-[#004AAD]'}`}>
                Detalle por plan
              </p>
              <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${textPri}`}>
                Compará funcionalidades y límites
              </h2>
              <p className={`text-sm max-w-2xl mx-auto ${textSec}`}>
                Qué incluye cada plan, explicado en palabras simples. Pasá el mouse o tocá una fila en celular para ver el detalle.
              </p>
              <p
                className={`text-xs max-w-2xl mx-auto mt-4 rounded-xl border px-3 py-2.5 leading-relaxed text-left sm:text-center ${
                  modoOscuro ? 'border-[#1F80FF]/30 bg-[#1F80FF]/10 text-gray-200' : 'border-[#1F80FF]/25 bg-[#1F80FF]/5 text-gray-700'
                }`}
              >
                <span className={`font-semibold ${textPri}`}>Básico</span>
                {' '}cubre el circuito de cobro: impagas, WhatsApp, gestiones, resumen del mes y calendario fiscal.
                {' '}
                <span className={`font-semibold ${textPri}`}>Profesional</span>
                {' '}agrega DSO y tendencias, recordatorios automáticos, link MP por factura, asistente y paquete para tu contador.
              </p>
            </div>

            <div className="lg:hidden space-y-3">
              <div className={`sticky top-20 z-20 grid grid-cols-3 gap-1.5 rounded-xl border p-1.5 shadow-sm ${box}`}>
                {orderedPlans.map((plan) => {
                  const isPopular = plan.isPopular || (plan.slug || '').toLowerCase() === 'profesional';
                  return (
                    <div
                      key={plan._id}
                      className={`rounded-lg px-1 py-2 text-center ${
                        isPopular
                          ? modoOscuro ? 'bg-[#004AAD]/30 text-[#5CA3FF]' : 'bg-[#004AAD]/10 text-[#004AAD]'
                          : textPri
                      }`}
                    >
                      <p className="text-[11px] font-bold leading-tight">{shortPlanName(plan)}</p>
                    </div>
                  );
                })}
              </div>

              <div className={`rounded-2xl border overflow-hidden ${box}`}>
                <p className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide ${textMuted} ${tableRowAlt}`}>
                  Límites incluidos
                </p>
                {limitRows.map((row) => (
                  <div key={row.key} className={`px-3 py-3 border-t ${modoOscuro ? 'border-gray-800' : 'border-gray-100'}`}>
                    <p className={`text-sm font-medium mb-2 ${textPri}`}>{row.label}</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {orderedPlans.map((plan) => (
                        <p key={plan._id + '-' + row.key} className={`text-center text-sm font-semibold leading-snug ${textPri}`}>
                          {row.value(plan)}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <p className={`px-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.16em] ${textMuted}`}>
                Funcionalidades por área
              </p>
              {featureGroups.map((group) => (
                <div key={group.title} className={`rounded-2xl border overflow-hidden ${box}`}>
                  <p className={`border-b px-3 py-2.5 text-xs font-bold ${modoOscuro ? 'border-gray-700 bg-[#004AAD]/20 text-[#5CA3FF]' : 'border-blue-100 bg-blue-50 text-[#004AAD]'}`}>
                    {group.title}
                  </p>
                  {group.features.map((featureKey, featureIndex) => (
                    <div key={featureKey} className={`px-3 py-3 ${featureIndex > 0 ? 'border-t' : ''} ${modoOscuro ? 'border-gray-800' : 'border-gray-100'}`}>
                      <p className={`text-sm font-medium mb-2 leading-snug ${textPri}`}>
                        {getFeatureName(featureKey, { short: true })}
                      </p>
                      <div className="grid grid-cols-3 gap-1.5">
                        {orderedPlans.map((plan) => (
                          <div key={plan._id + '-' + featureKey} className="flex justify-center">
                            {renderFeatureMark(isFeatureEnabledForPlan(plan, featureKey))}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <SpotlightCard className={`hidden lg:block rounded-2xl border shadow-sm overflow-hidden ${modoOscuro ? 'border-gray-800 bg-gray-900/40' : 'border-gray-200 bg-white'}`}>
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-left">
                  <colgroup>
                    <col className="w-[44%]" />
                    <col className="w-[18.66%]" />
                    <col className="w-[18.66%]" />
                    <col className="w-[18.66%]" />
                  </colgroup>
                  <thead className={tableHead}>
                    <tr>
                      <th className={`py-3.5 pl-4 pr-3 text-xs font-semibold uppercase tracking-wide ${tableHead}`}>
                        Característica
                      </th>
                      {orderedPlans.map((plan) => (
                        <th
                          key={plan._id}
                          className="py-3.5 px-3 text-xs font-semibold uppercase tracking-wide text-center"
                        >
                          {plan.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${divideY}`}>
                    <tr className={tableRowAlt}>
                      <td
                        colSpan={1 + orderedPlans.length}
                        className={`py-2.5 pl-4 pr-3 text-xs font-semibold uppercase tracking-wide ${textMuted}`}
                      >
                        Límites incluidos
                      </td>
                    </tr>
                    {limitRows.map((row) => (
                      <tr key={row.key}>
                        <td className={`py-3 pl-4 pr-3 text-sm leading-snug ${textSec}`}>
                          {row.label}
                        </td>
                        {orderedPlans.map((plan) => (
                          <td key={plan._id + '-' + row.key} className={`py-3 px-3 text-center text-sm font-medium ${textPri}`}>
                            {row.value(plan)}
                          </td>
                        ))}
                      </tr>
                    ))}

                    {featureGroups.map((group) => (
                      <React.Fragment key={group.title}>
                        <tr className={modoOscuro ? 'bg-[#004AAD]/20' : 'bg-blue-50'}>
                          <td
                            colSpan={1 + orderedPlans.length}
                            className={`border-y py-2.5 pl-4 pr-3 text-xs font-bold tracking-wide ${
                              modoOscuro
                                ? 'border-gray-700 text-[#5CA3FF]'
                                : 'border-blue-100 text-[#004AAD]'
                            }`}
                          >
                            {group.title}
                          </td>
                        </tr>
                        {group.features.map((featureKey) => (
                          <tr key={featureKey} className={modoOscuro ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50/80'}>
                            <td
                              className={`py-3 pl-4 pr-3 text-sm leading-snug ${textPri}`}
                              title={getFeatureName(featureKey)}
                            >
                              {getFeatureName(featureKey, { short: true })}
                            </td>
                            {orderedPlans.map((plan) => (
                              <td key={plan._id + '-' + featureKey} className="py-3 px-3 text-center">
                                {renderFeatureMark(isFeatureEnabledForPlan(plan, featureKey))}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </SpotlightCard>
          </div>

          <button
            type="button"
            onClick={() => {
              const container = document.getElementById('main-scroll');
              if (container) {
                container.scrollTo({ top: 0, behavior: 'smooth' });
                activeSectionRef.current = 0;
                window.__planesActiveSection = 0;
              }
            }}
            className={`hidden lg:flex absolute bottom-8 right-8 w-12 h-12 rounded-full items-center justify-center transition-all duration-300 group z-20 ${modoOscuro ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}`}
            aria-label="Volver arriba"
          >
            <FiArrowRight className="w-5 h-5 rotate-[-90deg] group-hover:-translate-y-1 transition-transform" />
          </button>
        </div>
      </section>

        {/* Modal de suscripción muy simple */}
        {selectedPlan && (() => {
          const isRenewalCheckout =
            regularizePayment && currentPlanId && selectedPlan._id === currentPlanId;
          return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className={`w-full max-w-md rounded-2xl p-6 shadow-2xl border ${box}`}>
              <div className="mb-4 flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1F80FF]/10 text-[#1F80FF]">
                  <FiCheck className="h-4 w-4" />
                </div>
                <div>
                  <h3 className={`text-base font-semibold ${textPri}`}>
                    {isRenewalCheckout
                      ? 'Regularizar suscripción'
                      : isPlanUpgrade(selectedPlan)
                      ? 'Actualizar plan'
                      : 'Confirmar suscripción'}
                  </h3>
                  <p className={`text-xs ${textMuted}`}>
                    {selectedPlan.name} — {formatPlanPrice(selectedPlan, billingCycle)}/
                    {getPlanBillingPeriodLabel(billingCycle)}
                  </p>
                </div>
              </div>
              <p className={`mb-4 text-sm ${textSec}`}>
                {isRenewalCheckout
                  ? 'Tu cuenta y tus datos en Contsuite se mantienen. Te redirigimos a Mercado Pago para autorizar el cobro y recuperar el acceso completo.'
                  : isPlanUpgrade(selectedPlan)
                  ? `Vas a pagar con Mercado Pago y acceder al plan ${selectedPlan.name}. Podés cancelar desde Mercado Pago cuando quieras.`
                  : `Te redirigimos a Mercado Pago para autorizar el plan ${selectedPlan.name}. El Básico incluye 30 días de prueba al alta.`}
              </p>
              <div className="mb-5">
                <AcceptTermsField
                  checked={acceptTerms}
                  onChange={setAcceptTerms}
                  tc={{ textSec, modoOscuro }}
                />
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPlan(null);
                    setAcceptTerms(false);
                  }}
                  disabled={checkoutLoading}
                  className={`flex-1 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${btnOut}`}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmSubscription}
                  disabled={checkoutLoading || !acceptTerms}
                  className="flex-1 rounded-full bg-[#1F80FF] px-4 py-2 text-sm font-semibold text-white hover:bg-[#004AAD] disabled:opacity-50"
                >
                  {checkoutLoading ? 'Redirigiendo…' : 'Ir a Mercado Pago'}
                </button>
              </div>
            </div>
          </div>
          );
        })()}
      </div>
    </MarketingLayout>
  );
};

export default Planes;






